import { fetchUtils } from 'ra-core';
import { API_URL, resolveImageUrl, buildApiUrl } from './config/api';

const apiUrl = API_URL;

// Convert relative image URLs to full URLs for admin panel
const convertImageUrls = (item) => {
  if (item.images && Array.isArray(item.images)) {
    item.images = item.images.map(image => ({
      ...image,
      url: resolveImageUrl(image.url)
    }));
  }
  return item;
};

const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  // Add JWT token to requests
  const token = localStorage.getItem('token');
  if (token) {
    options.headers.set('Authorization', `Bearer ${token}`);
  }
  
  return fetchUtils.fetchJson(url, options);
};

const dataProvider = {
  getList: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      page: page,
      limit: perPage,
      sortBy: field,
      sortOrder: order.toLowerCase(),
      ...params.filter,
    };

    if (query.q && !query.search) {
      query.search = query.q;
      delete query.q;
    }

    // Remove undefined/empty filter values so backend defaults work
    Object.keys(query).forEach((key) => {
      if (
        query[key] === undefined ||
        query[key] === null ||
        query[key] === '' ||
        query[key] === 'undefined'
      ) {
        delete query[key];
      }
    });

    // Use admin endpoint for reviews
    let url;
    if (resource === 'reviews') {
      url = `${apiUrl}/${resource}/admin/all?${new URLSearchParams(query).toString()}`;
    } else {
      url = `${apiUrl}/${resource}?${new URLSearchParams(query).toString()}`;
    }

    return httpClient(url).then(({ headers, json }) => {
      // Handle different response formats for different resources
      let data, total;
      
      if (resource === 'products') {
        data = json.products || json.data || json;
        total = json.pagination?.totalProducts || json.total || data.length;
      } else if (resource === 'orders') {
        // Orders endpoint now returns structured data
        data = Array.isArray(json) ? json : json.data || json;
        total = json.total || data.length;
      } else if (resource === 'users') {
        // Users endpoint now returns structured data
        data = Array.isArray(json) ? json : json.data || json;
        total = json.total || data.length;
      } else if (resource === 'reviews') {
        // For reviews, use admin endpoint
        data = json.reviews || json.data || json;
        total = json.pagination?.totalReviews || json.total || data.length;
      } else {
        data = json.data || json;
        total = json.total || data.length;
      }

      const normalizedData = data.map(item => {
        const normalizedItem = convertImageUrls({ ...item, id: item._id });

        if (resource === 'reviews') {
          if (item.productId && typeof item.productId === 'object') {
            normalizedItem.product = {
              ...item.productId,
              id: item.productId._id
            };
            normalizedItem.productId = item.productId._id;
          }

          if (item.userId && typeof item.userId === 'object') {
            normalizedItem.user = {
              ...item.userId,
              id: item.userId._id
            };
            normalizedItem.userId = item.userId._id;
          }
        }

        return normalizedItem;
      });

      return {
        data: normalizedData,
        total
      };
    });
  },

  getOne: (resource, params) => {
    let url;
    if (resource === 'products') {
      url = buildApiUrl(`${resource}/id/${params.id}`);
    } else if (resource === 'reviews') {
      url = buildApiUrl(`${resource}/admin/${params.id}`);
    } else {
      url = buildApiUrl(`${resource}/${params.id}`);
    }
    
    return httpClient(url).then(({ json }) => ({
      data: convertImageUrls({ ...json, id: json._id }),
    }));
  },

  getMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const url = buildApiUrl(`${resource}?${new URLSearchParams(query).toString()}`);
    return httpClient(url).then(({ json }) => {
      const data = json.products || json.data || json;
      const items = Array.isArray(data)
        ? data
        : Array.isArray(json.products)
          ? json.products
          : [];

      return { data: items.map(item => convertImageUrls({ ...item, id: item._id })) };
    });
  },

  getManyReference: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      page: page,
      limit: perPage,
      sortBy: field,
      sortOrder: order.toLowerCase(),
      [params.target]: params.id,
      ...params.filter,
    };

    const url = buildApiUrl(`${resource}?${new URLSearchParams(query).toString()}`);

    return httpClient(url).then(({ json }) => {
      const data = json.products || json.data || json;
      const items = Array.isArray(data)
        ? data
        : Array.isArray(json.products)
          ? json.products
          : [];
      return {
        data: items.map(item => convertImageUrls({ ...item, id: item._id })),
        total: json.total || items.length,
      };
    });
  },

  create: (resource, params) => {
    const { id, ...data } = params.data;

    if (resource === 'orders') {
      return httpClient(buildApiUrl('orders/admin/manual'), {
        method: 'POST',
        body: JSON.stringify(data),
      }).then(({ json }) => {
        const orderData = json.order
          ? { ...json.order, id: json.order.id || json.order._id }
          : { id: json.id };
        return { data: orderData };
      });
    }

    return httpClient(buildApiUrl(resource), {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(({ json }) => ({
      data: convertImageUrls({ ...json, id: json._id }),
    }));
  },

  update: (resource, params) => {
    const { id, action, ...data } = params.data;

    // Handle order processing actions
    if (resource === 'orders' && action) {
      return httpClient(buildApiUrl(`${resource}/${params.id}/process`), {
        method: 'PUT',
        body: JSON.stringify({ action }),
      }).then(({ json }) => ({
        data: { ...json.order, id: json.order._id },
      }));
    }

    // Handle B2B application actions (approve/reject)
    if (resource === 'b2b-applications' && action) {
      return httpClient(buildApiUrl(`${resource}/${params.id}/${action}`), {
        method: 'PUT',
        body: JSON.stringify(data),
      }).then(({ json }) => ({
        data: { ...json.application, id: json.application._id, temporaryPassword: json.temporaryPassword, existingAccount: json.existingAccount },
      }));
    }

    // Regular update
    return httpClient(buildApiUrl(`${resource}/${params.id}`), {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then(({ json }) => ({
      data: convertImageUrls({ ...json, id: json._id }),
    }));
  },

  updateMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(buildApiUrl(`${resource}?${new URLSearchParams(query).toString()}`), {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: params.ids }));
  },

  delete: (resource, params) =>
    httpClient(buildApiUrl(`${resource}/${params.id}`), {
      method: 'DELETE',
    }).then(({ json }) => ({ data: convertImageUrls({ ...json, id: json._id }) })),

  deleteMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(buildApiUrl(`${resource}?${new URLSearchParams(query).toString()}`), {
      method: 'DELETE',
    }).then(({ json }) => ({ data: params.ids }));
  },
};

export default dataProvider;
